interface EditorJson {
  current_id: number;
  children: ElementJson[];
}

interface ElementJson {
  type: string;
  data: {
    id?: string;
    children?: ElementJson[];
    [key: string]: unknown;
  };
}

interface DocumentRecord {
  id: string;
  currentId: number;
  cameraJson: string;
  updatedAt: number;
}

interface ElementRecord {
  key: string;
  docId: string;
  elementId: string;
  parentId: string | null;
  zIndex: number;
  payload: ElementJson;
  updatedAt: number;
}

interface SnapshotEntry {
  signature: string;
  record: ElementRecord;
}

export interface LoadedDocument {
  editorJson: string;
  cameraJson: string;
}

class EditorStorageService {
  private dbName = 'tylas_editor_db';
  private dbVersion = 1;
  private documentsStore = 'documents';
  private elementsStore = 'elements';
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private readonly rootParentKey = '__root__';
  private lastSnapshotById = new Map<string, SnapshotEntry>();
  private lastCurrentId = 0;
  private lastCameraJson = '';

  private resolveElementZIndex(element: ElementJson): number {
    const raw = element?.data?.z_index;
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0.5;
  }

  public async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (event) => {
        reject(event);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(this.documentsStore)) {
          db.createObjectStore(this.documentsStore, { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains(this.elementsStore)) {
          const store = db.createObjectStore(this.elementsStore, { keyPath: 'key' });
          store.createIndex('docId', 'docId', { unique: false });
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve();
      };
    });

    return this.initPromise;
  }

  public async loadDocument(docId = 'default'): Promise<LoadedDocument | null> {
    await this.init();
    const db = this.db;
    if (!db) return null;

    const [doc, elements] = await Promise.all([
      this.readDocumentRecord(docId),
      this.readElementRecordsByDocId(docId),
    ]);

    if (!doc && elements.length === 0) {
      this.lastSnapshotById.clear();
      this.lastCurrentId = 0;
      this.lastCameraJson = '';
      return null;
    }

    const editorJson = this.buildEditorJson(doc?.currentId ?? 0, elements);
    this.lastSnapshotById = this.buildSnapshotFromRecords(elements);
    this.lastCurrentId = doc?.currentId ?? 0;
    this.lastCameraJson = doc?.cameraJson ?? '';

    return {
      editorJson,
      cameraJson: doc?.cameraJson ?? '',
    };
  }

  public async persistFromEditorJson(
    editorJson: string,
    cameraJson: string,
    docId = 'default',
  ): Promise<void> {
    await this.init();
    const db = this.db;
    if (!db) return;

    const parsed = this.parseEditorJson(editorJson);
    if (!parsed) return;

    const nextSnapshotById = this.buildSnapshotFromEditor(docId, parsed.children);
    const changedRecords: ElementRecord[] = [];
    const deletedKeys: string[] = [];

    nextSnapshotById.forEach((entry, elementId) => {
      const prev = this.lastSnapshotById.get(elementId);
      if (!prev || prev.signature !== entry.signature) {
        changedRecords.push(entry.record);
      }
    });

    this.lastSnapshotById.forEach((entry, elementId) => {
      if (!nextSnapshotById.has(elementId)) {
        deletedKeys.push(entry.record.key);
      }
    });

    const shouldUpdateDoc =
      parsed.current_id !== this.lastCurrentId || cameraJson !== this.lastCameraJson;
    if (!shouldUpdateDoc && changedRecords.length === 0 && deletedKeys.length === 0) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([this.documentsStore, this.elementsStore], 'readwrite');
      const docs = tx.objectStore(this.documentsStore);
      const elements = tx.objectStore(this.elementsStore);

      if (shouldUpdateDoc) {
        const docRecord: DocumentRecord = {
          id: docId,
          currentId: parsed.current_id,
          cameraJson,
          updatedAt: Date.now(),
        };
        docs.put(docRecord);
      }

      for (const record of changedRecords) {
        elements.put(record);
      }

      for (const key of deletedKeys) {
        elements.delete(key);
      }

      tx.oncomplete = () => resolve();
      tx.onerror = (event) => reject(event);
      tx.onabort = (event) => reject(event);
    });

    this.lastSnapshotById = nextSnapshotById;
    this.lastCurrentId = parsed.current_id;
    this.lastCameraJson = cameraJson;
  }

  private readDocumentRecord(docId: string): Promise<DocumentRecord | null> {
    return new Promise((resolve, reject) => {
      if (!this.db) return resolve(null);
      const tx = this.db.transaction([this.documentsStore], 'readonly');
      const store = tx.objectStore(this.documentsStore);
      const request = store.get(docId);

      request.onsuccess = () => resolve((request.result as DocumentRecord | undefined) ?? null);
      request.onerror = (event) => reject(event);
    });
  }

  private readElementRecordsByDocId(docId: string): Promise<ElementRecord[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) return resolve([]);
      const tx = this.db.transaction([this.elementsStore], 'readonly');
      const store = tx.objectStore(this.elementsStore);
      const index = store.index('docId');
      const request = index.getAll(docId);

      request.onsuccess = () => resolve((request.result as ElementRecord[] | undefined) ?? []);
      request.onerror = (event) => reject(event);
    });
  }

  private parseEditorJson(editorJson: string): EditorJson | null {
    try {
      const parsed = JSON.parse(editorJson) as EditorJson;
      if (!Array.isArray(parsed.children)) {
        return null;
      }
      if (typeof parsed.current_id !== 'number') {
        parsed.current_id = 0;
      }
      return parsed;
    } catch (error) {
      console.error('EditorStorage: failed to parse editor json', error);
      return null;
    }
  }

  private buildSnapshotFromEditor(
    docId: string,
    children: ElementJson[],
  ): Map<string, SnapshotEntry> {
    const snapshot = new Map<string, SnapshotEntry>();
    this.flattenElements(docId, null, children, snapshot);
    return snapshot;
  }

  private flattenElements(
    docId: string,
    parentId: string | null,
    elements: ElementJson[],
    snapshot: Map<string, SnapshotEntry>,
  ): void {
    elements.forEach(element => {
      const elementId = element?.data?.id;
      if (!elementId || typeof elementId !== 'string') {
        return;
      }

      const payload = this.normalizePayload(element);
      const record: ElementRecord = {
        key: this.recordKey(docId, elementId),
        docId,
        elementId,
        parentId,
        zIndex: this.resolveElementZIndex(payload),
        payload,
        updatedAt: Date.now(),
      };
      snapshot.set(elementId, {
        signature: this.buildSignature(record),
        record,
      });

      if (element.type === 'Group' && Array.isArray(element.data.children)) {
        this.flattenElements(docId, elementId, element.data.children, snapshot);
      }
    });
  }

  private normalizePayload(element: ElementJson): ElementJson {
    const payload = JSON.parse(JSON.stringify(element)) as ElementJson;
    if (payload.type === 'Group') {
      payload.data.children = [];
    }
    return payload;
  }

  private buildSignature(record: ElementRecord): string {
    return JSON.stringify({
      parentId: record.parentId,
      zIndex: record.zIndex,
      payload: record.payload,
    });
  }

  private buildSnapshotFromRecords(records: ElementRecord[]): Map<string, SnapshotEntry> {
    const snapshot = new Map<string, SnapshotEntry>();
    for (const rawRecord of records) {
      const record: ElementRecord = {
        ...rawRecord,
        zIndex:
          typeof rawRecord.zIndex === 'number' && Number.isFinite(rawRecord.zIndex)
            ? rawRecord.zIndex
            : this.resolveElementZIndex(rawRecord.payload),
      };
      snapshot.set(record.elementId, {
        signature: this.buildSignature(record),
        record,
      });
    }
    return snapshot;
  }

  private buildEditorJson(currentId: number, records: ElementRecord[]): string {
    const byId = new Map<string, ElementRecord>();
    const childrenByParent = new Map<string, ElementRecord[]>();

    for (const record of records) {
      byId.set(record.elementId, record);
      const parentKey = record.parentId ?? this.rootParentKey;
      const bucket = childrenByParent.get(parentKey) ?? [];
      bucket.push(record);
      childrenByParent.set(parentKey, bucket);
    }

    const sortByZIndex = (a: ElementRecord, b: ElementRecord) => a.zIndex - b.zIndex;
    childrenByParent.forEach(bucket => bucket.sort(sortByZIndex));

    const buildElement = (record: ElementRecord): ElementJson | null => {
      const payload = JSON.parse(JSON.stringify(record.payload)) as ElementJson;
      if (!payload || !payload.type || !payload.data) {
        return null;
      }

      if (payload.type === 'Group') {
        const childRecords = childrenByParent.get(record.elementId) ?? [];
        payload.data.children = childRecords
          .map(childRecord => buildElement(byId.get(childRecord.elementId) ?? childRecord))
          .filter((child): child is ElementJson => child !== null);
      }
      return payload;
    };

    const roots = (childrenByParent.get(this.rootParentKey) ?? [])
      .map(record => buildElement(record))
      .filter((element): element is ElementJson => element !== null);

    return JSON.stringify({
      current_id: currentId,
      children: roots,
    } as EditorJson);
  }

  private recordKey(docId: string, elementId: string): string {
    return `${docId}:${elementId}`;
  }
}

export const editorStorage = new EditorStorageService();
